import { NavLink, useNavigate } from "react-router-dom"
import { authService } from "../../services/authService"

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
}

const navItems: NavItem[] = [
  {
    label: "Dashboard",
    path: "/admin/dashboard",
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    label: "Farmers",
    path: "/admin/farmers",
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    label: "Map View",
    path: "/admin/map",
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
  },
  {
    label: "Tree Types",
    path: "/admin/tree-types",
    icon: (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c-1.5 3-4 5-4 8a4 4 0 008 0c0-3-2.5-5-4-8z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v10M9 18l3 3 3-3" />
      </svg>
    ),
  },
]

export default function Sidebar({ collapsed }: SidebarProps) {
  const navigate = useNavigate()

  const handleLogout = async () => {
    await authService.logout()
    navigate("/admin/akm-login", { replace: true })
  }

  return (
    <aside
      className="fixed left-0 top-0 h-screen bg-[#0d0d0d] border-r border-white/[0.06] flex flex-col z-40 transition-all duration-300 overflow-hidden"
      style={{ width: collapsed ? "64px" : "224px" }}
    >
      {/* Brand */}
      <div className="border-b border-white/[0.06] flex items-center min-h-[64px] px-3">
        {collapsed ? (
          /* Collapsed — show small logo icon only */
          <div className="w-8 h-8 flex items-center justify-center mx-auto">
            <img
              src="https://voag.tv/images/logo.png"
              alt="VOAG"
              className="w-8 h-8 object-contain"
              onError={e => {
                (e.target as HTMLImageElement).style.display = "none"
              }}
            />
          </div>
        ) : (
          /* Expanded — full logo + subtitle */
          <div className="flex items-center gap-3 overflow-hidden">
            <img
              src="https://voag.tv/images/logo.png"
              alt="VOAG"
              className="h-9 w-auto object-contain flex-shrink-0"
              onError={e => {
                (e.target as HTMLImageElement).style.display = "none"
              }}
            />
            <div className="overflow-hidden whitespace-nowrap">
              <p className="text-white text-sm font-bold tracking-tight leading-none">Voag Admin</p>
              <p className="text-neutral-600 text-[10px] mt-0.5">Tree Registry</p>
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto overflow-x-hidden">
        {navItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                  : "text-neutral-500 hover:text-neutral-200 hover:bg-white/[0.04] border border-transparent"
              }`
            }
          >
            {item.icon}
            {!collapsed && (
              <span className="overflow-hidden whitespace-nowrap">{item.label}</span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="px-2 py-4 border-t border-white/[0.06]">
        <button
          onClick={handleLogout}
          title={collapsed ? "Sign Out" : undefined}
          className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-sm font-medium text-neutral-600 hover:text-red-400 hover:bg-red-500/5 border border-transparent hover:border-red-500/10 transition-all"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {!collapsed && (
            <span className="overflow-hidden whitespace-nowrap">Sign Out</span>
          )}
        </button>
      </div>
    </aside>
  )
}